import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Grid, Header, Segment, Card, Icon, Statistic, Image, Button, Divider, Message } from 'semantic-ui-react';
import { AuthStdFooter } from "../../utils/auth-std-footer";
import { getMenuIdForFeature } from '../../utils/roleUtils';
import LiteraryGroupBookList from '../../components/LiteraryGroupBookList';
import BookCreatorStateManager from '../../components/BookCreatorStateManager';
import AddLiteraryGroupPanel from '../../components/panels/AddLiteraryGroupPanel';
import AddBookPanel from '../../components/panels/AddBookPanel';
import ImportStoryPanel from '../../components/panels/ImportStoryPanel';
import {
  selectAllLitGrpTreeView,
  selectDirectLitGroupsStatus
} from '../../slices/userLitGroupAssetsSlice';
import { fetchStatistics, selectStatisticsByView, selectStatisticsLoading } from '../../slices/statisticsSlice';
import { STAT_SETS } from '../../constants/statisticsFlags';
import useIsLoggedIn from '../../isLoggedIn';
import '../../styles/landing-pages.css';
import '../../styles/article.css';
import bookStack from '../../assets/justBookStack.svg'
import hourGlass from '../../assets/hourGlassLine.svg'
import checkBox from '../../assets/checkListLine.svg'
import myLibrary from '../../assets/myLibrary.svg'
import importStory from '../../assets/importStory.svg'
import profileIcon from '../../assets/profileLineGlasses.svg'
import myProfileLine from '../../assets/myProfileLine.svg'
import lightening from '../../assets/lightening.svg'

const AddBooksLandingPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authToken = useIsLoggedIn();
  const userProfile = useSelector((state) => state.user.profile);
  const userName = userProfile?.data?.title || 'Book Creator';

  // Get literary groups data from Redux (for the list component)
  const litGroupsTree = useSelector(selectAllLitGrpTreeView);
  const directLitGroupsStatus = useSelector(selectDirectLitGroupsStatus);

  // Get statistics from Redux using the new statistics system
  const libraryStats = useSelector(selectStatisticsByView('library_overview'));
  const isLoadingStats = useSelector(selectStatisticsLoading('library_overview'));

  // Panel state management
  const [activePanel, setActivePanel] = useState(null);
  const [panelContext, setPanelContext] = useState({});
  const [selectedLitGroup, setSelectedLitGroup] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);

  // Initialize page data
  useEffect(() => {
    if (authToken) {
      // Fetch statistics for library overview
      dispatch(fetchStatistics({
        viewType: 'library_overview',
        flags: STAT_SETS.LIBRARY_OVERVIEW
      }));
    }
  }, [dispatch, authToken]);

  // Event handlers for BookCreatorStateManager
  const handleLitGroupSelectState = (litGroup) => {
    setSelectedLitGroup(litGroup);
  };

  const handleBookSelectState = (book) => {
    setSelectedBook(book);
  };

  const handlePdfSelectState = (pdf) => {
    console.log('PDF selected:', pdf);
  };

  // New panel opening handler
  const handleOpenPanel = useCallback((panelType, context = {}) => {
    setActivePanel(panelType);
    setPanelContext(context);
  }, []);

  // Panel close handlers
  const handleClosePanel = useCallback(() => {
    setActivePanel(null);
    setPanelContext({});
  }, []);

  // Panel success handlers
  const handleLitGroupSuccess = useCallback((litGroup) => {
    setSelectedLitGroup(litGroup);
    // Data will be refreshed by the panel component
  }, []);

  const handleBookSuccess = useCallback((book) => {
    setSelectedBook(book);
    // Data will be refreshed by the panel component
  }, []);

  const handleImportSuccess = useCallback((importData) => {
    console.log('Import successful:', importData);
    // Handle import success if needed
  }, []);

  const handleStateChange = useCallback((state) => {
    // Update context for panels when state changes
    if (state.litGroup) setSelectedLitGroup(state.litGroup);
    if (state.book) setSelectedBook(state.book);
  }, []);

  // Primary action buttons for book creation workflow
  const quickActions = [
    {
      icon: 'users',
      img: myLibrary,
      title: 'Add Literary Group',
      description: 'Create a new literary group',
      link: '/add-literary-group-landing'
    },
    {
      icon: 'book',
      img: bookStack,
      title: 'Add Book',
      description: 'Add a book to existing group',
      link: '/add-book-landing'
    },
    {
      icon: 'upload',
      img: importStory,
      title: 'Import Story',
      description: 'Upload and import a story',
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('storyImporter') }
    },
    {
      icon: 'users',
      img: myLibrary,
      title: 'Opportunity',
      description: 'Manage Opportunity Marketplace',
      link: '/manage-opportunities-landing'
    }
    // , {
    //   icon: 'help circle',
    //   img: profileIcon,
    //   title: 'Help & Support',
    //   description: 'Get help with book creation',
    //   link: '/dashboard',
    //   state: { menuSelection: getMenuIdForFeature('bookDetails') }
    // }
  ];

  // Statistics for the overview section - using new statistics system
  const statistics = [
    {
      label: 'Literary Groups',
      value: isLoadingStats ? '...' : (libraryStats?.litGroupCount || '0'),
      icon: 'users',
      img: myLibrary,
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('bookCreatorWorksheet') }
    },
    {
      label: 'Total Books',
      value: isLoadingStats ? '...' : (libraryStats?.bookCount || '0'),
      icon: 'book',
      img: bookStack,
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('bookDetails') }
    },
    {
      label: 'In Progress',
      value: isLoadingStats ? '...' : (libraryStats?.inProgressCount || '0'),
      icon: 'hourglass half',
      img: hourGlass,
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('bookDetails') }
    },
    {
      label: 'Completed',
      value: isLoadingStats ? '...' : (libraryStats?.completedCount || '0'),
      icon: 'check circle',
      img: checkBox,
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('bookDetails') }
    },
    {
      label: 'My Profile',
      value: '',
      icon: 'user',
      img: profileIcon,
      link: '/profile'
    }
  ];

  // Mock function for handling literary group selection
  const handleLitGroupSelect = (group) => {
    console.log('Selected literary group:', group);
    // This would normally trigger navigation or form opening
  };

  // Mock function for handling book selection
  const handleBookSelect = (book, group) => {
    console.log('Selected book:', book, 'from group:', group);
    // This would normally trigger navigation or form opening
  };

  return (
    <Container fluid className="landing-page book-creator-landing">
      <Container style={{ marginTop: "30px" }}>
        <Grid className="articleListWidth">
          <Grid.Row>
            <Grid.Column width={3}>
              <div className="img-with-gradient">
                <Image src="/static/media/shutterstock_2294396055-1024x1024.2d3b550af78ebc4d2e7b.jpg" />
              </div>
            </Grid.Column>
            <Grid.Column width={13} textAlign="center">
              <span className="blgListItemTitle">Welcome, {userName}</span>
              <span className="blgListSubTitle">Text Library</span>
              <p className="landing-subheading-text">
                Create and manage your literary groups and books from this centralized hub.
                Add new literary groups, create books, import existing stories, and organize your creative works.
                Use the quick actions below to start your book creation workflow.
              </p>
              <div style={{ textAlign: 'left', marginTop: '1em' }}>
                <Button
                  as={Link}
                  to="/dashboard"
                  size="large"
                  className="landing-goto-button"
                >
                  Go to Main Dashboard
                </Button>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Grid className="articleListWidth" style={{ marginTop: "20px", borderTop: "solid 1px #AAAAAA" }}></Grid>
      </Container>

      <Container style={{ marginTop: '2em', marginBottom: '2em' }}>
        <Grid>
          <Grid.Row>
            <Grid.Column width={6}>
              <Header as="h2">
                <img src={myProfileLine} alt="" />
                <Header.Content style={{
                  fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                  fontWeight: '400',
                  fontSize: '2.5rem'
                }}>
                  Library Overview
                  <Header.Subheader>Literary groups and books at a glance</Header.Subheader>
                </Header.Content>
              </Header>
            </Grid.Column>
            <Grid.Column width={10}>
              <div>
                <Statistic.Group widths="five">
                  {statistics.map((stat, index) => {
                    const StatisticContent = (
                      <>
                        <Statistic.Value>
                          <div style={{
                            fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                            fontWeight: '300',
                            fontSize: '3.0rem'
                          }}><img src={stat.img} alt="" /> {stat.value}</div>
                        </Statistic.Value>
                        <Statistic.Label style={{
                          fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                          fontWeight: '300',
                          fontSize: '1.1rem'
                        }}>{stat.label}</Statistic.Label>
                      </>
                    );

                    if (stat.link) {
                      return (
                        <Statistic
                          key={index}
                          as={Link}
                          to={stat.link}
                          state={stat.state}
                          className="statistic-link"
                        >
                          {StatisticContent}
                        </Statistic>
                      );
                    }

                    return (
                      <Statistic key={index}>
                        {StatisticContent}
                      </Statistic>
                    );
                  })}
                </Statistic.Group>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Divider />

        {/* <Header as="h2" dividing style={{ marginTop: '2em' }}>
          <img src={lightening} alt="" />
          <Header.Content>
            Quick Actions
            <Header.Subheader>Start your book creation workflow</Header.Subheader>
          </Header.Content>
        </Header> */}

        {/* <Card.Group itemsPerRow={4} stackable>
          {quickActions.map((action, index) => (
            <Card
              key={index}
              as={Link}
              to={action.link}
              state={action.state}
              raised
            >
              <Card.Content>
                <img src={action.img} alt="" style={{ height: '80px', display: 'block', margin: '0 auto 1em', color: '#2c5d8a' }} />
                <Card.Header textAlign="center">{action.title}</Card.Header>
                <Card.Description textAlign="center">
                  {action.description}
                </Card.Description>
              </Card.Content>
            </Card>
          ))}
        </Card.Group> */}

        <Grid columns={1} stackable style={{ marginTop: '2em' }}>
          <Grid.Column>
            <Header as="h2" dividing>
              <img src={bookStack} alt="" style={{ height: '50px' }} />
              <Header.Content>
                Your Literary Groups & Books
                <Header.Subheader>Select literary groups, books, and manage PDF files</Header.Subheader>
              </Header.Content>
            </Header>

            <BookCreatorStateManager
              onLitGroupSelect={handleLitGroupSelectState}
              onBookSelect={handleBookSelectState}
              onPdfSelect={handlePdfSelectState}
              onOpenPanel={handleOpenPanel}
              onStateChange={handleStateChange}
            />
          </Grid.Column>
        </Grid>

        {/* <Grid columns={1} stackable style={{ marginTop: '2em' }}>
          <Grid.Column>
            <Header as="h2" dividing>
              <img src={bookStack} alt="" style={{ height: '50px' }} />
              <Header.Content>
                Your Literary Groups & Books
                <Header.Subheader>Manage your existing literary groups and their books</Header.Subheader>
              </Header.Content>
            </Header>

            {directLitGroupsStatus === 'loading' ? (
              <Segment>
                <div style={{ textAlign: 'center', padding: '2em' }}>
                  <Icon name="spinner" loading size="large" />
                  <p>Loading your literary groups...</p>
                </div>
              </Segment>
            ) : (
              <LiteraryGroupBookList
                onLitGroupSelect={handleLitGroupSelect}
                onBookSelect={handleBookSelect}
              />
            )}

            {directLitGroupsStatus === 'succeeded' && (!litGroupsTree || !litGroupsTree.children || litGroupsTree.children.length === 0) && (
              <Segment>
                <Message info>
                  <Message.Header>No Literary Groups Found</Message.Header>
                  <p>You haven't created any literary groups yet. Click "Add Literary Group" above to get started!</p>
                </Message>
              </Segment>
            )}
          </Grid.Column>
        </Grid> */}

        <Grid columns={2} stackable style={{ marginTop: '2em' }}>
          <Grid.Column>
            <Header as="h2" dividing>
              <img src={myLibrary} alt="" style={{ height: '50px' }} />
              <Header.Content>
                Getting Started
                <Header.Subheader>Tips for organizing your books</Header.Subheader>
              </Header.Content>
            </Header>

            <Segment>
              <div style={{ marginBottom: '1em' }}>
                <Header as="h4">
                  <Icon name="lightbulb" color="yellow" />
                  <Header.Content>Pro Tips</Header.Content>
                </Header>
                <p>• Create literary groups to organize books by genre, series, or theme</p>
                <p>• Use the Import Story feature for existing manuscripts</p>
                <p>• Add characters and voice variations to bring your stories to life</p>
              </div>
              <div style={{ textAlign: 'center', marginTop: '1em' }}>
                <Button
                  as={Link}
                  to="/dashboard"
                  state={{ menuSelection: getMenuIdForFeature('bookCreatorWorksheet') }}
                  compact
                  primary
                >
                  Create Your First Literary Group
                </Button>
              </div>
            </Segment>
          </Grid.Column>

          <Grid.Column>
            <Header as="h2" dividing>
              <img src={importStory} alt="" style={{ height: '50px' }} />
              <Header.Content>
                Import & Create
                <Header.Subheader>Multiple ways to add content</Header.Subheader>
              </Header.Content>
            </Header>

            <Segment>
              <div style={{ marginBottom: '1em' }}>
                <Header as="h4">
                  <Icon name="upload" color="blue" />
                  <Header.Content>Import Options</Header.Content>
                </Header>
                <p>• Upload existing manuscripts in various formats</p>
                <p>• Create books from scratch with our editor</p>
                <p>• Import character lists and voice assignments</p>
              </div>
              <div style={{ textAlign: 'center', marginTop: '1em' }}>
                <Button
                  as={Link}
                  to="/dashboard"
                  state={{ menuSelection: getMenuIdForFeature('storyImporter') }}
                  compact
                  color="blue"
                >
                  Import Your First Story
                </Button>
              </div>
            </Segment>
          </Grid.Column>
        </Grid>
      </Container>

      {/* Slide-in Panels */}
      <AddLiteraryGroupPanel
        isOpen={activePanel === 'add-group'}
        onClose={handleClosePanel}
        onSuccess={handleLitGroupSuccess}
      />

      <AddBookPanel
        isOpen={activePanel === 'add-book'}
        onClose={handleClosePanel}
        onSuccess={handleBookSuccess}
        litGroup={panelContext.litGroup || selectedLitGroup}
      />

      <ImportStoryPanel
        isOpen={activePanel === 'import'}
        onClose={handleClosePanel}
        onSuccess={handleImportSuccess}
        litGroup={panelContext.litGroup || selectedLitGroup}
        book={panelContext.book || selectedBook}
      />

      {AuthStdFooter()}
    </Container>
  );
};

export default AddBooksLandingPage;
